import { useState, useEffect } from "react";
import { AdminLayout } from "@/project/jain_elibrary-admin/components/layout/AdminLayout";
import { Button } from "@/project/jain_elibrary-admin/components/ui/button";
import { Input } from "@/project/jain_elibrary-admin/components/ui/input";
import { Label } from "@/project/jain_elibrary-admin/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/jain_elibrary-admin/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/jain_elibrary-admin/components/ui/card";
import {
  Download,
  Loader2,
  FileText,
  FileSpreadsheet,
  FileJson,
  Filter,
  Search,
  Eye,
} from "lucide-react";
import { adminService, Statistics } from "../../services/adminService";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";
import { getPreviewCountMatchingStatistics } from "./exportPreviewStats";

type ContentType =
  | "books"
  | "articles"
  | "audio"
  | "manuscripts"
  | "magazines"
  | "agam"
  | "all";
type ExportFormat = "csv" | "excel" | "json";

type ContentTypeSingle = Exclude<ContentType, "all">;

const PREVIEW_TYPE_LABELS: Record<ContentTypeSingle, string> = {
  books: "Books",
  articles: "Articles",
  audio: "Audio",
  manuscripts: "Manuscripts",
  magazines: "Magazines",
  agam: "Agam",
};

function formatPreviewCountsByType(
  counts: Partial<Record<ContentTypeSingle, number>>
): string {
  return (Object.entries(counts) as [ContentTypeSingle, number][])
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${PREVIEW_TYPE_LABELS[k]}: ${n.toLocaleString()}`)
    .join(" · ");
}

interface ExportFilters {
  contentType: ContentType;
  language?: string;
  search?: string;
}

interface ExportProgress {
  currentPage: number;
  totalPages: number;
  fetchedRecords: number;
  status: string;
  currentType?: string;
}

// Download CSV function
const downloadCSVFile = (csvData: string, filename: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (!csvData || csvData.length === 0) {
        reject(new Error("CSV data is empty"));
        return;
      }

      const BOM = "\uFEFF";
      const csvWithBOM = BOM + csvData;
      const blob = new Blob([csvWithBOM], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${filename}.csv`;
      link.style.display = "none";
      document.body.appendChild(link);

      requestAnimationFrame(() => {
        try {
          link.click();
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link);
            }
            URL.revokeObjectURL(url);
            resolve();
          }, 200);
        } catch (clickError) {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(url);
          reject(clickError);
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};

// Download JSON function
const downloadJSONFile = (jsonData: any, filename: string): void => {
  const jsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Download Excel (CSV with .xlsx extension - simplified)
const downloadExcelFile = (
  csvData: string,
  filename: string
): Promise<void> => {
  // For now, we'll use CSV format but with .xlsx extension
  // In production, you might want to use a library like xlsx
  return downloadCSVFile(csvData, filename.replace(".xlsx", ""));
};

const ExportElibrary = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<ExportFilters>({
    // Default to books so preview totals match "book count" unless user explicitly includes all types
    contentType: "books",
    language: undefined,
    search: undefined,
  });
  const [exportFormat, setExportFormat] = useState<ExportFormat>("csv");
  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    currentPage: 0,
    totalPages: 0,
    fetchedRecords: 0,
    status: "",
  });
  const [previewResults, setPreviewResults] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewCountsByType, setPreviewCountsByType] = useState<
    Partial<Record<ContentTypeSingle, number>>
  >({});
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    adminService
      .getStatistics()
      .then(setStatistics)
      .catch(() => {});
  }, []);

  // Hardcoded list of 11 languages available in the system
  const languages = [
    { name: "Apabhramsha" },
    { name: "Braj" },
    { name: "Devnagari" },
    { name: "Dhundari" },
    { name: "English" },
    { name: "Germany" },
    { name: "Gujarati" },
    { name: "Hindi" },
    { name: "Marathi" },
    { name: "Prakrita" },
    { name: "Sanskrit" },
  ];

  // Fetch all records for a given content type
  const fetchAllRecords = async (
    type: ContentType,
    params: any = {}
  ): Promise<any[]> => {
    const allRecords: any[] = [];
    let page = 1;
    const pageSize = 500;
    let hasMore = true;
    let totalCount = 0;
    let estimatedPages = 0;

    while (hasMore) {
      try {
        setExportProgress({
          currentPage: page,
          totalPages: estimatedPages ?? page,
          fetchedRecords: allRecords.length,
          status: `Fetching ${type} - page ${page}...`,
          currentType: type,
        });

        let response: any;

        switch (type) {
          case "books":
            response = await adminService.getBooks({
              page,
              page_size: pageSize,
              ...params,
            });
            break;
          case "articles":
            response = await adminService.getArticles({
              page,
              page_size: pageSize,
              ...params,
            });
            break;
          case "audio":
            response = await adminService.getAudio({
              page,
              page_size: pageSize,
              ...params,
            });
            break;
          case "manuscripts":
            response = await adminService.getManuscripts({
              page,
              page_size: pageSize,
              ...params,
            });
            break;
          case "magazines":
            response = await adminService.getMagazines({
              page,
              page_size: pageSize,
              ...params,
            });
            break;
          case "agam":
            response = await adminService.getAgam({
              page,
              page_size: pageSize,
              ...params,
            });
            break;
          default:
            return [];
        }

        if (page === 1 && response.count) {
          totalCount = response.count;
          estimatedPages = Math.ceil(totalCount / pageSize);
        }

        allRecords.push(...response.results);

        if (!response.next) {
          hasMore = false;
        } else {
          page++;
        }
      } catch (error: any) {
        console.error(`Error fetching ${type} page ${page}:`, error);
        showErrorToast(error, `Failed to fetch ${type} page ${page}`);
        hasMore = false;
      }
    }

    return allRecords;
  };

  // Client-side search filter function for books (backend search not working for books)
  const filterBooksBySearch = (books: any[], searchTerm: string): any[] => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return books;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return books.filter(book => {
      // Search in multiple fields
      const titleEnglish = (book.title_english ?? "").toLowerCase();
      const titleLanguage = (book.title_language ?? "").toLowerCase();
      const author = (book.author ?? "").toLowerCase();
      const publisher = (book.publisher ?? "").toLowerCase();
      const srNo = (book.sr_no ?? "").toString().toLowerCase();
      const editor = (book.editor ?? "").toLowerCase();
      const translator = (book.translator ?? "").toLowerCase();

      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      return (
        titleEnglish.includes(searchLower) ||
        titleLanguage.includes(searchLower) ||
        author.includes(searchLower) ||
        publisher.includes(searchLower) ||
        srNo.includes(searchLower) ||
        editor.includes(searchLower) ||
        translator.includes(searchLower)
      );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    });
  };

  // Convert records to CSV - includes ALL columns
  const convertToCSV = (records: any[]): string => {
    if (records.length === 0) {
      return "";
    }

    // Get all unique keys from records - include ALL fields
    const allKeys = new Set<string>();
    records.forEach(record => {
      Object.keys(record).forEach(key => {
        // Exclude internal metadata fields that start with underscore
        if (!key.startsWith("_")) {
          allKeys.add(key);
        }
      });
    });

    // Sort keys for consistent column order
    const headers = Array.from(allKeys).sort();

    if (headers.length === 0) {
      return "";
    }

    // Create CSV rows
    const rows = records.map(record => {
      return headers.map(header => {
        const value = record[header];
        const stringValue =
          value !== null && value !== undefined ? String(value) : "";
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    return [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
  };

  // Get all records based on filters
  const getAllRecords = async (): Promise<any[]> => {
    const allRecords: any[] = [];
    const types: ContentType[] =
      filters.contentType === "all"
        ? ["books", "articles", "audio", "manuscripts", "magazines", "agam"]
        : [filters.contentType];

    const params: any = {};
    // Language filter - case insensitive (backend handles with icontains)
    if (
      filters.language &&
      filters.language !== "all" &&
      filters.language.trim()
    ) {
      params.language = filters.language.trim();
    }

    // Ensure search is properly included - check for non-empty string after trim
    const searchValue = filters.search?.trim();
    if (searchValue && searchValue.length > 0) {
      params.search = searchValue;
    }

    for (const type of types) {
      let records: any[] = [];

      // For books with search, we need to fetch all pages first, then filter client-side
      // (backend search doesn't work for books) - use same logic as preview
      if (type === "books" && searchValue && searchValue.length > 0) {
        // Fetch all books without search param (since backend doesn't support it)
        // But keep language filter if present
        const booksParams: any = {};
        if (params.language) {
          booksParams.language = params.language;
        }

        // Fetch all pages (same as preview logic)
        const allBooks: any[] = [];
        let page = 1;
        const pageSize = 500;
        let hasMore = true;
        const maxPages = 100; // Allow fetching all pages for export

        while (hasMore && page <= maxPages) {
          try {
            setExportProgress({
              currentPage: page,
              totalPages: maxPages,
              fetchedRecords: allBooks.length,
              status: `Fetching books page ${page} for filtering...`,
              currentType: type,
            });

            const pageParams = { ...booksParams, page, page_size: pageSize };
            const pageResponse = await adminService.getBooks(pageParams);
            allBooks.push(...(pageResponse.results ?? []));

            if (!pageResponse.next || pageResponse.results.length < pageSize) {
              hasMore = false;
            } else {
              page++;
            }
          } catch (error) {
            console.error(`Error fetching books page ${page}:`, error);
            hasMore = false;
          }
        }

        // Apply client-side search filter (same as preview)
        const beforeFilter = allBooks.length;
        records = filterBooksBySearch(allBooks, searchValue);
        const afterFilter = records.length;
        console.log(
          `Export - Books filtering: ${beforeFilter} -> ${afterFilter} records (search: "${searchValue}", language: "${params.language ?? "all"}")`
        );
      } else {
        // For other types or books without search, use normal fetch
        records = await fetchAllRecords(type, params);
      }

      // Add type identifier to each record
      records.forEach(record => {
        record._content_type = type;
      });
      allRecords.push(...records);
    }

    console.log(
      `getAllRecords returning ${allRecords.length} total records (search: "${searchValue ?? "none"}")`
    );
    return allRecords;
  };

  // Preview filtered results
  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setShowPreview(false);
      setPreviewCountsByType({});
      toast.info("Loading filtered results...");

      // Fetch first page to get total count and preview
      const types: ContentType[] =
        filters.contentType === "all"
          ? ["books", "articles", "audio", "manuscripts", "magazines", "agam"]
          : [filters.contentType];

      const params: any = {
        page: 1,
        page_size: 100, // Show first 100 results in preview
      };
      // Language filter - case insensitive (backend handles for all types including Agam)
      if (
        filters.language &&
        filters.language !== "all" &&
        filters.language.trim()
      ) {
        params.language = filters.language.trim();
      }

      const searchValue = filters.search?.trim();
      if (searchValue && searchValue.length > 0) {
        params.search = searchValue;
      }

      /** List API `count` can differ from /statistics/ aggregates; use statistics when unfiltered. */
      const hasListFilters =
        Boolean(searchValue) ||
        Boolean(filters.language?.trim() && filters.language !== "all");

      const allPreviewRecords: any[] = [];
      let totalCount = 0;
      const countsByType: Partial<Record<ContentTypeSingle, number>> = {};

      /** Fetch each type in parallel so total wait ≈ slowest request, not the sum. */
      const loadPreviewForType = async (
        type: ContentTypeSingle
      ): Promise<{ type: ContentTypeSingle; response: any }> => {
        try {
          let response: any;
          switch (type) {
            case "books":
              response = await adminService.getBooks(params);
              if (searchValue && searchValue.length > 0) {
                const booksParams: any = {};
                if (params.language) {
                  booksParams.language = params.language;
                }

                const allBooks: any[] = [];
                let page = 1;
                const pageSize = 500;
                let hasMore = true;
                const maxPages = 100;

                while (hasMore && page <= maxPages) {
                  try {
                    const pageParams = {
                      ...booksParams,
                      page,
                      page_size: pageSize,
                    };
                    const pageResponse =
                      await adminService.getBooks(pageParams);
                    allBooks.push(...(pageResponse.results ?? []));

                    if (
                      !pageResponse.next ||
                      pageResponse.results.length < pageSize
                    ) {
                      hasMore = false;
                    } else {
                      page++;
                    }
                  } catch (error) {
                    console.error(`Error fetching books page ${page}:`, error);
                    hasMore = false;
                  }
                }

                const filteredBooks = filterBooksBySearch(
                  allBooks,
                  searchValue
                );
                response = {
                  count: filteredBooks.length,
                  results: filteredBooks.slice(0, 100),
                  next: filteredBooks.length > 100,
                };
              }
              break;
            case "articles":
              response = await adminService.getArticles(params);
              break;
            case "audio":
              response = await adminService.getAudio(params);
              break;
            case "manuscripts":
              response = await adminService.getManuscripts(params);
              break;
            case "magazines":
              response = await adminService.getMagazines(params);
              break;
            case "agam":
              response = await adminService.getAgam(params);
              break;
            default:
              return { type, response: null };
          }
          return { type, response };
        } catch (error: any) {
          console.error(`Error fetching ${type} preview:`, error);
          return { type, response: null };
        }
      };

      const previewParts = await Promise.all(
        types.map(t => loadPreviewForType(t as ContentTypeSingle))
      );

      for (const { type, response } of previewParts) {
        if (response) {
          const booksWithClientSearch =
            type === "books" && searchValue && searchValue.length > 0;
          let typeCount = response.count ?? 0;
          if (!booksWithClientSearch) {
            typeCount = getPreviewCountMatchingStatistics(
              type,
              statistics,
              typeCount,
              hasListFilters
            );
          }
          countsByType[type] = typeCount;
          totalCount += typeCount;
          const records = response.results ?? [];
          records.forEach((record: any) => {
            record._content_type = type;
          });
          allPreviewRecords.push(...records);
        }
      }

      setPreviewResults(allPreviewRecords);
      setPreviewTotal(totalCount);
      setPreviewCountsByType(countsByType);
      setShowPreview(true);

      if (totalCount === 0) {
        toast.warning("No records found matching your filters");
      } else {
        toast.success(
          `Found ${totalCount.toLocaleString()} record(s). Showing first ${allPreviewRecords.length} in preview.`
        );
      }
    } catch (error: any) {
      console.error("Error previewing results:", error);
      showErrorToast(error, "Failed to load preview. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: 0,
        status: "Starting export...",
      });

      const searchValue = filters.search?.trim();
      console.log("ExportElibrary handleExport - filters:", filters);
      console.log("ExportElibrary handleExport - searchValue:", searchValue);
      console.log(
        "ExportElibrary handleExport - contentType:",
        filters.contentType
      );

      toast.info("Fetching e-library data... This may take a few minutes.");

      // Always use getAllRecords which applies proper filtering
      // This ensures only filtered results are exported (especially for books with client-side filtering)
      const allRecords = await getAllRecords();

      console.log(
        "ExportElibrary handleExport - allRecords.length:",
        allRecords.length
      );
      console.log("ExportElibrary handleExport - previewTotal:", previewTotal);

      if (allRecords.length === 0) {
        toast.error("No records found to export");
        setExporting(false);
        return;
      }

      // Verify that we're exporting the correct number of records
      if (
        searchValue &&
        searchValue.length > 0 &&
        previewTotal > 0 &&
        allRecords.length !== previewTotal
      ) {
        console.warn(
          `Export count mismatch: preview shows ${previewTotal} but export has ${allRecords.length}`
        );
        toast.warning(
          `Exporting ${allRecords.length} records (preview showed ${previewTotal})`
        );
      }

      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `elibrary_export_${timestamp}`;

      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: allRecords.length,
        status: `Converting ${allRecords.length} records to ${exportFormat.toUpperCase()}...`,
      });

      if (exportFormat === "json") {
        downloadJSONFile(allRecords, filename);
      } else if (exportFormat === "csv" || exportFormat === "excel") {
        // Group by content type for better organization
        const groupedRecords: Record<string, any[]> = {};
        allRecords.forEach(record => {
          const type = record._content_type ?? "unknown";
          if (!groupedRecords[type]) {
            groupedRecords[type] = [];
          }
          groupedRecords[type].push(record);
        });

        // Create CSV for each type or combined
        if (filters.contentType === "all") {
          // Export each type separately
          for (const [type, records] of Object.entries(groupedRecords)) {
            const csvContent = convertToCSV(records);
            if (exportFormat === "csv") {
              await downloadCSVFile(csvContent, `${filename}_${type}`);
            } else {
              await downloadExcelFile(csvContent, `${filename}_${type}.xlsx`);
            }
            // Small delay between downloads
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } else {
          // Single export
          const csvContent = convertToCSV(allRecords);
          if (exportFormat === "csv") {
            await downloadCSVFile(csvContent, filename);
          } else {
            await downloadExcelFile(csvContent, `${filename}.xlsx`);
          }
        }
      }

      toast.success(
        `Successfully exported ${allRecords.length} records in ${exportFormat.toUpperCase()} format`
      );
    } catch (error: any) {
      console.error("Error exporting data:", error);
      showErrorToast(error, "Failed to export data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return "0 Bytes";
    }
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getTotalRecords = (): number => {
    if (!statistics) {
      return 0;
    }
    if (filters.contentType === "all") {
      return (
        (statistics.total_books ?? 0) +
        (statistics.total_articles ?? 0) +
        (statistics.total_audio ?? 0) +
        (statistics.total_manuscripts ?? 0) +
        (statistics.total_magazines ?? 0) +
        (statistics.total_agam ?? 0)
      );
    }
    switch (filters.contentType) {
      case "books":
        return statistics.total_books ?? 0;
      case "articles":
        return statistics.total_articles ?? 0;
      case "audio":
        return statistics.total_audio ?? 0;
      case "manuscripts":
        return statistics.total_manuscripts ?? 0;
      case "magazines":
        return statistics.total_magazines ?? 0;
      case "agam":
        return statistics.total_agam ?? 0;
      default:
        return 0;
    }
  };

  /** Unfiltered totals from /statistics/ — ignores language & search */
  const totalRecordsFromStatistics = getTotalRecords();
  /**
   * After Preview, use the same filtered count as the list API (matches export).
   * Before Preview, show statistics only (can disagree with preview when filters apply).
   */
  const displayedExportRecordCount = showPreview
    ? previewTotal
    : totalRecordsFromStatistics;
  const hasActiveTextFilters = [
    filters.language && filters.language !== "all",
    filters.search?.trim(),
  ].some(Boolean);
  const estimatedFileSize =
    displayedExportRecordCount > 0
      ? formatFileSize(displayedExportRecordCount * 500)
      : "—";

  return (
    <AdminLayout>
      <div className="min-w-0 space-y-4 md:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground md:text-3xl">
              Export E-Library Data
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Export books, articles, audio, manuscripts, and magazines with
              custom filters
            </p>
          </div>
        </div>

        {/* Filters Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Export Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="content-type">Content Type</Label>
                <Select
                  value={filters.contentType}
                  onValueChange={value =>
                    setFilters({
                      ...filters,
                      contentType: value as ContentType,
                    })
                  }
                >
                  <SelectTrigger id="content-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Content Types</SelectItem>
                    <SelectItem value="books">Books</SelectItem>
                    <SelectItem value="articles">Articles</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                    <SelectItem value="manuscripts">Manuscripts</SelectItem>
                    <SelectItem value="magazines">Magazines</SelectItem>
                    <SelectItem value="agam">Agam</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={filters.language ?? "all"}
                  onValueChange={value =>
                    setFilters({
                      ...filters,
                      language: value === "all" ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger id="language">
                    <SelectValue placeholder="All Languages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Languages</SelectItem>
                    {languages.map(language => (
                      <SelectItem key={language.name} value={language.name}>
                        {language.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Input
                    id="search"
                    placeholder="Search in titles, authors..."
                    value={filters.search ?? ""}
                    onChange={e =>
                      setFilters({
                        ...filters,
                        search: e.target.value || undefined,
                      })
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" && !previewLoading) {
                        e.preventDefault();
                        handlePreview();
                      }
                    }}
                    className="pr-10"
                  />
                  {filters.search && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters(prev => ({ ...prev, search: undefined }));
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-4">
              {filters.search && filters.search.trim().length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Active search:{" "}
                  <span className="font-medium text-foreground">
                    &quot;{filters.search}&quot;
                  </span>
                </div>
              )}
              <Button
                onClick={handlePreview}
                disabled={previewLoading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {previewLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Preview Results
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Results Table */}
        {showPreview && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Filtered Results Preview
                </span>
                <span className="max-w-xl text-right text-sm font-normal text-muted-foreground">
                  {filters.contentType === "all" ? (
                    <span className="flex flex-col items-end gap-1">
                      <span className="text-xs leading-snug">
                        {formatPreviewCountsByType(previewCountsByType) ||
                          "No counts per type"}
                      </span>
                      <span>
                        {previewTotal.toLocaleString()} total record(s) — sum of
                        all types above
                      </span>
                      <span className="text-xs text-muted-foreground/90">
                        Preview lists up to 100 rows per type (concatenated)
                      </span>
                    </span>
                  ) : (
                    <>
                      {previewTotal.toLocaleString()} total record(s) found
                      {PREVIEW_TYPE_LABELS[filters.contentType] && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({PREVIEW_TYPE_LABELS[filters.contentType]})
                        </span>
                      )}
                      {previewResults.length < previewTotal &&
                        ` (Showing first ${previewResults.length})`}
                    </>
                  )}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {previewResults.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No results found matching your filters
                </div>
              ) : (
                <div
                  className="w-full overflow-x-auto overflow-y-auto rounded-lg border"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <table
                    className="table-fixed border-collapse"
                    style={{
                      minWidth: "800px",
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead className="bg-muted">
                      <tr>
                        <th className="h-12 w-20 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Type
                        </th>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          SR No
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Title (English)
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Title (Native)
                        </th>
                        <th className="h-12 min-w-[150px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Author
                        </th>
                        <th className="h-12 min-w-[120px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Publisher
                        </th>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Language
                        </th>
                        <th className="h-12 w-20 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Year
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewResults.map((record, index) => (
                        <tr
                          key={`${record._content_type}-${record.sr_no ?? record.all_serial ?? index}`}
                          className="border-b transition-colors hover:bg-muted/50"
                        >
                          <td className="whitespace-nowrap p-4 align-middle">
                            <span className="rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                              {record._content_type ?? "unknown"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle font-mono text-xs">
                            {record.sr_no ?? record.all_serial ?? "-"}
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={
                                record.title_english ??
                                record.title_en ??
                                record.scripture_name ??
                                "-"
                              }
                            >
                              {record.title_english ??
                                record.title_en ??
                                record.scripture_name ??
                                "-"}
                            </div>
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={
                                record.title_language ??
                                record.title_native ??
                                record.mool_scripture_name ??
                                record.trans_scripture_name ??
                                "-"
                              }
                            >
                              {record.title_language ??
                                record.title_native ??
                                record.mool_scripture_name ??
                                record.trans_scripture_name ??
                                "-"}
                            </div>
                          </td>
                          <td className="min-w-[150px] p-4 align-middle">
                            <div
                              className="max-w-[200px] truncate"
                              title={record.author ?? "-"}
                            >
                              {record.author ?? "-"}
                            </div>
                          </td>
                          <td className="min-w-[120px] p-4 align-middle">
                            <div
                              className="max-w-[180px] truncate"
                              title={record.publisher ?? "-"}
                            >
                              {record.publisher ?? "-"}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {record.language ??
                              record.trans_language ??
                              record.mool_language ??
                              "-"}
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {record.pub_year ?? record.publication_year ?? "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Export Format Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Export Format</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 overflow-x-hidden">
            <div className="grid w-full min-w-0 grid-cols-1 gap-3 xl:grid-cols-[repeat(3,minmax(0,1fr))]">
              <Button
                variant={exportFormat === "csv" ? "default" : "outline"}
                onClick={() => setExportFormat("csv")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileText className="h-4 w-4 shrink-0" />
                CSV
              </Button>
              <Button
                variant={exportFormat === "excel" ? "default" : "outline"}
                onClick={() => setExportFormat("excel")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileSpreadsheet className="h-4 w-4 shrink-0" />
                Excel
              </Button>
              <Button
                variant={exportFormat === "json" ? "default" : "outline"}
                onClick={() => setExportFormat("json")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileJson className="h-4 w-4 shrink-0" />
                JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export Button and Progress */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <Button
                onClick={handleExport}
                disabled={exporting || loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                size="lg"
              >
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export E-Library Data
                  </>
                )}
              </Button>

              {exporting && exportProgress.status && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {exportProgress.status}
                  </p>
                  {exportProgress.totalPages > 0 && (
                    <div className="h-2 w-full rounded-full bg-muted">
                      <div
                        className="h-2 rounded-full bg-primary transition-all duration-300"
                        style={{
                          width: `${Math.min(
                            (exportProgress.currentPage /
                              exportProgress.totalPages) *
                              100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  )}
                  {exportProgress.fetchedRecords > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {exportProgress.fetchedRecords.toLocaleString()} records
                      fetched
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Export Information */}
        <Card>
          <CardHeader>
            <CardTitle>Export Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                • Total Records:{" "}
                {loading
                  ? "Loading..."
                  : displayedExportRecordCount.toLocaleString()}
                {showPreview && (
                  <span className="ml-1 text-xs text-primary">
                    (matches preview & export filters)
                  </span>
                )}
              </p>
              {!showPreview && hasActiveTextFilters && (
                <p className="text-xs text-amber-800 dark:text-amber-200/90">
                  The total shown is from library statistics and does not
                  include language or search. Use Preview Results to see how
                  many rows match your filters.
                </p>
              )}
              <p>• Format: {exportFormat.toUpperCase()}</p>
              <p>• Estimated File Size: {estimatedFileSize}</p>
              <p>
                • Content Type:{" "}
                {filters.contentType === "all"
                  ? "All Types"
                  : filters.contentType}
              </p>
              {filters.language && (
                <p>• Language Filter: {filters.language} (case insensitive)</p>
              )}
              {filters.search && <p>• Search: {filters.search}</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ExportElibrary;
